/**
 * @architecture-header 1
 * @layer module-infrastructure
 * @domain settings-users
 * @responsibility Own the backend-authoritative SET-F05 organization member directory query and its
 *   create/update commands, reusing IAM-F10's operations verbatim from the Settings Users tab.
 * @state-owner TanStack Query
 * @allowed-imports app environment/session; active-role provider; same-module API and contracts;
 *   shared me-context loader; TanStack Query
 * @forbidden component UI; fabricated organization data outside the environment.demoMode branch;
 *   direct Supabase calls; email/provider adapters
 * @public-api settings-users hooks
 * @side-effects authenticated member reads/writes and cache invalidation
 * @tests frontend/packages/workspace-web/src/modules/settings-users/api/settings-users.queries.test.tsx
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { VispoApiError } from '@vispo/api-client';
import type {
  OrganizationMember,
  OrganizationMemberCreateInput,
  OrganizationMemberDirectory,
  OrganizationMemberUpdateInput,
} from '@vispo/contracts';
import { useOptionalWorkspaceEnvironment } from '../../../app/config/environment';
import { useActiveOrganizationId } from '../../../app/providers/ActiveRoleProvider';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import { loadMeContext } from '../../auth';
import {
  createOrganizationMember,
  listOrganizationMembers,
  updateOrganizationMember,
} from './settings-users';

const DEMO_ORGANIZATION_ID = 'demo-organization';

const DEMO_MEMBERS: readonly OrganizationMember[] = Object.freeze([
  {
    membershipId: 'agent-a-set-f05-owner-membership',
    userId: 'agent-a-set-f05-owner',
    displayName: '王老師',
    role: 'owner',
    state: 'active',
    joinedAt: '2026-08-01T08:00:00Z',
    endedAt: null,
  },
  {
    membershipId: 'agent-a-set-f05-admin-membership',
    userId: 'agent-a-set-f05-admin',
    displayName: '林老師',
    role: 'admin',
    state: 'active',
    joinedAt: '2026-08-02T08:00:00Z',
    endedAt: null,
  },
  {
    membershipId: 'agent-a-set-f05-removed-membership',
    userId: 'agent-a-set-f05-removed',
    displayName: '陳老師',
    role: 'staff',
    state: 'removed',
    joinedAt: '2026-08-03T08:00:00Z',
    endedAt: '2026-08-10T00:00:00Z',
  },
]) as unknown as readonly OrganizationMember[];

function demoDirectory(): OrganizationMemberDirectory {
  return {
    organizationId: DEMO_ORGANIZATION_ID,
    revision: 3,
    members: DEMO_MEMBERS.map((member) => ({ ...member })),
  } as OrganizationMemberDirectory;
}

export const settingsUsersKeys = {
  users: (userId: string, organizationId: string) =>
    ['set-f05', 'users', userId, organizationId] as const,
};

function effectiveOrganizationId(
  activeOrganizationId: string | null,
  demoMode: boolean | undefined,
): string | null {
  return activeOrganizationId ?? (demoMode ? DEMO_ORGANIZATION_ID : null);
}

function forcedNonAdmin(): boolean {
  return Boolean(
    (globalThis as typeof globalThis & { __VISPO_SET_F05_FORCE_NON_ADMIN__?: boolean })
      .__VISPO_SET_F05_FORCE_NON_ADMIN__,
  );
}

function forcedRevisionConflict(): boolean {
  return Boolean(
    (globalThis as typeof globalThis & { __VISPO_SET_F05_FORCE_REVISION_CONFLICT__?: boolean })
      .__VISPO_SET_F05_FORCE_REVISION_CONFLICT__,
  );
}

export interface OrganizationMembersAccess {
  readonly organizationId: string | null;
  readonly canManage: boolean;
}

/** Mirrors the backend: `list_organization_members` is itself owner/admin-gated, so a non-admin
 * gets the same blocked state as the roster read, never a read-only view (CONTRACT READY, IO
 * pre-ruling #4). */
export function useOrganizationMembersAccess() {
  const environment = useOptionalWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const activeOrganizationId = useActiveOrganizationId();
  const organizationId = effectiveOrganizationId(activeOrganizationId, environment?.demoMode);

  return useQuery({
    queryKey: ['set-f05', 'users-access', session?.userId ?? 'anonymous', organizationId ?? 'none'],
    enabled: Boolean(environment && (session || environment.demoMode)),
    queryFn: async (): Promise<OrganizationMembersAccess> => {
      if (environment?.demoMode) {
        return { organizationId, canManage: !forcedNonAdmin() };
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      const context = await loadMeContext(environment.apiClient);
      const organization = context.organizations.find(
        (candidate) => candidate.id === organizationId,
      );
      return {
        organizationId: organization?.id ?? null,
        canManage: Boolean(
          organization && ['owner', 'admin'].includes(organization.membership_role),
        ),
      };
    },
  });
}

export function useOrganizationMembers(organizationId: string | null, enabled: boolean) {
  const environment = useOptionalWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const userId = session?.userId ?? 'anonymous';

  return useQuery({
    queryKey: settingsUsersKeys.users(userId, organizationId ?? 'none'),
    enabled: Boolean(environment && organizationId && enabled),
    queryFn: async (): Promise<OrganizationMemberDirectory> => {
      if (environment?.demoMode) return demoDirectory();
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      return listOrganizationMembers(environment.apiClient, organizationId);
    },
    retry: 1,
  });
}

function useMemberCommandContext() {
  const environment = useOptionalWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const activeOrganizationId = useActiveOrganizationId();
  const organizationId = effectiveOrganizationId(activeOrganizationId, environment?.demoMode);
  const userId = session?.userId ?? 'anonymous';
  const queryClient = useQueryClient();
  const queryKey = settingsUsersKeys.users(userId, organizationId ?? 'none');
  return { environment, organizationId, queryClient, queryKey };
}

function invalidateMemberQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  queryKey: readonly unknown[],
) {
  void queryClient.invalidateQueries({ queryKey });
}

export function useCreateOrganizationMember() {
  const { environment, organizationId, queryClient, queryKey } = useMemberCommandContext();
  return useMutation({
    mutationFn: async (
      input: Pick<OrganizationMemberCreateInput, 'userId' | 'role'>,
    ): Promise<OrganizationMemberDirectory> => {
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      if (environment?.demoMode) {
        const current = queryClient.getQueryData<OrganizationMemberDirectory>(queryKey);
        if (!current) throw new Error('組織成員尚未準備完成');
        if (forcedRevisionConflict()) {
          throw new VispoApiError(
            {
              code: 'ORGANIZATION_REVISION_CONFLICT',
              message: '資料已更新，請重新整理後再試。',
              details: undefined,
              requestId: 'demo',
            },
            409,
          );
        }
        if (current.members.some((member) => member.userId === input.userId)) {
          throw new VispoApiError(
            {
              code: 'ORGANIZATION_MEMBER_DUPLICATE',
              message: '此使用者已經是組織成員。',
              details: undefined,
              requestId: 'demo',
            },
            409,
          );
        }
        const created: OrganizationMember = {
          membershipId: `demo-membership-${crypto.randomUUID()}`,
          userId: input.userId,
          displayName: input.userId,
          role: input.role,
          state: 'active',
          joinedAt: new Date().toISOString(),
          endedAt: null,
        } as OrganizationMember;
        return {
          ...current,
          revision: current.revision + 1,
          members: [...current.members, created],
        } as OrganizationMemberDirectory;
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      const current = queryClient.getQueryData<OrganizationMemberDirectory>(queryKey);
      if (!current) throw new Error('組織成員尚未準備完成');
      await createOrganizationMember(environment.apiClient, organizationId, {
        ...input,
        expectedRevision: current.revision,
        idempotencyKey: `agent-a-set-f05-${crypto.randomUUID()}`,
      });
      return listOrganizationMembers(environment.apiClient, organizationId);
    },
    onSuccess: (directory) => {
      if (environment?.demoMode) queryClient.setQueryData(queryKey, directory);
      else invalidateMemberQueries(queryClient, queryKey);
    },
  });
}

export function useUpdateOrganizationMember() {
  const { environment, organizationId, queryClient, queryKey } = useMemberCommandContext();
  return useMutation({
    mutationFn: async (
      input: { userId: string } & Pick<OrganizationMemberUpdateInput, 'role' | 'state'>,
    ): Promise<OrganizationMemberDirectory> => {
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      const current = queryClient.getQueryData<OrganizationMemberDirectory>(queryKey);
      if (!current) throw new Error('組織成員尚未準備完成');
      if (environment?.demoMode) {
        if (forcedRevisionConflict()) {
          throw new VispoApiError(
            {
              code: 'ORGANIZATION_REVISION_CONFLICT',
              message: '資料已更新，請重新整理後再試。',
              details: undefined,
              requestId: 'demo',
            },
            409,
          );
        }
        const target = current.members.find((member) => member.userId === input.userId);
        if (!target) {
          throw new VispoApiError(
            {
              code: 'ORGANIZATION_MEMBER_NOT_FOUND',
              message: '找不到這位成員。',
              details: undefined,
              requestId: 'demo',
            },
            404,
          );
        }
        const updatedMember: OrganizationMember = {
          ...target,
          role: input.role ?? target.role,
          state: input.state ?? target.state,
          endedAt: input.state === 'removed' ? new Date().toISOString() : null,
        };
        return {
          ...current,
          revision: current.revision + 1,
          members: current.members.map((member) =>
            member.userId === input.userId ? updatedMember : member,
          ),
        } as OrganizationMemberDirectory;
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      const { userId, ...changes } = input;
      await updateOrganizationMember(environment.apiClient, organizationId, userId, {
        ...changes,
        expectedRevision: current.revision,
        idempotencyKey: `agent-a-set-f05-${crypto.randomUUID()}`,
      });
      return listOrganizationMembers(environment.apiClient, organizationId);
    },
    onSuccess: (directory) => {
      if (environment?.demoMode) queryClient.setQueryData(queryKey, directory);
      else invalidateMemberQueries(queryClient, queryKey);
    },
  });
}
